import { useMemo } from 'react';
import { IconID } from '@hub/common/Icon/Icon';
import { FileExtension } from '@hub/common/hooks/useFileDownload';
import { fileType } from '@hub/bearing-hub/common/constants';
import { IbhShape, type IbhWidenDto } from '@igus/iglidur-bearing-hub-api';
import type { DownloadTabDefinition } from '@hub/bearing-hub/common/downloadTab/DownloadTab';
import { useTranslation } from 'next-i18next';

export const useDownloadTab = (widens: IbhWidenDto[]) => {
  const assets = widens?.filter(widen => !!widen.description);
  const { t } = useTranslation(['bearing-hub/bearingHub']);

  const staticDownloadOptions = [
    {
      icon: IconID.DOWNLOAD_CAD,
      text: t(`DOWNLOAD_BUTTON.STEP`, { ns: 'bearing-hub/bearingHub' }),
      fileExtension: FileExtension.STP,
      fileLinkType: fileType.stp,
      notSupportedShapes: [],
    },
    {
      icon: IconID.DOWNLOAD_CAD,
      text: t(`DOWNLOAD_BUTTON.STL`, { ns: 'bearing-hub/bearingHub' }),
      fileExtension: FileExtension.STL,
      fileLinkType: fileType.stl,
      notSupportedShapes: [],
    },
    {
      icon: IconID.DOWNLOAD_UNKNOWN,
      text: t(`DOWNLOAD_BUTTON.TECHNICAL_DRAWING`, { ns: 'bearing-hub/bearingHub' }),
      fileExtension: FileExtension.PDF,
      fileLinkType: fileType.drawing,
      notSupportedShapes: [
        IbhShape.FL,
        IbhShape.VFL,
        IbhShape.Y,
        IbhShape.YK,
        IbhShape.TE,
        IbhShape.D,
        IbhShape.VF,
        IbhShape.K,
        IbhShape.VS,
        IbhShape.FSG,
      ],
    },
    {
      icon: IconID.DOWNLOAD_PDF,
      text: t(`DOWNLOAD_BUTTON.PDF_REPORT`, { ns: 'bearing-hub/bearingHub' }),
      fileExtension: FileExtension.PDF,
      fileLinkType: fileType.report,
      notSupportedShapes: [],
    },
  ].filter(download => !!download.text);

  const dynamicDownloadOptions = useMemo(() => {
    if (!assets?.length) {
      return [];
    }

    return assets.map(asset => ({
      icon: IconID.DOWNLOAD_UNKNOWN,
      externalId: asset.externalId,
      text: asset.description,
      fileExtension: 'pdf',
      notSupportedShapes: [],
    }));
  }, [assets]);

  const downloadTabDefinition = useMemo(
    () => [...staticDownloadOptions, ...dynamicDownloadOptions] as DownloadTabDefinition[],
    [staticDownloadOptions, dynamicDownloadOptions]
  );

  return {
    downloadTabDefinition,
  };
};
